import React, { useEffect, useState, useCallback } from 'react';
import {
    X, Phone, AlertTriangle, HeartPulse, ShieldAlert,
    User, Users, Stethoscope, Loader2,
} from 'lucide-react';
import residencesService from '../../../services/carehome_manager/residencesService';

// ── helpers ───────────────────────────────────────────────────────────────────
const has = (v) => v != null && String(v).trim() !== '';
const val = (v) => (has(v) ? String(v).trim() : '—');

// Match the mobile sheet's "d MMM yyyy" date format.
const fmtDob = (raw) => {
    if (!has(raw)) return '—';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── presentational bits ───────────────────────────────────────────────────────
const Field = ({ label, value, trailing, strong }) => (
    <div>
        <p className="text-[10.5px] font-semibold tracking-wide text-slate-400 dark:text-gray-500 uppercase">{label}</p>
        <p className={`mt-0.5 leading-snug text-slate-800 dark:text-gray-200 ${strong ? 'text-[15px] font-bold' : 'text-[13.5px] font-semibold'}`}>
            {value}
            {has(trailing) && <span className="ml-1.5 text-[12px] font-medium text-slate-400 dark:text-gray-500">{trailing}</span>}
        </p>
    </div>
);

const InfoCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white dark:bg-[#262b33] border border-[#E5EAF3] dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
        <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" />
            <h3 className="text-[12px] font-bold tracking-wide text-slate-600 dark:text-gray-400 uppercase">{title}</h3>
        </div>
        <div className="flex flex-col gap-3">{children}</div>
    </div>
);

// Strong clinical alert block — the things responders must not miss.
const AlertCard = ({ icon: Icon, title, value, tone = 'red' }) => {
    const t = tone === 'amber'
        ? { border: 'border-[#FCD34D] dark:border-amber-900/50', bg: 'bg-[#FEF6E7] dark:bg-amber-900/10', head: 'text-[#B45309] dark:text-amber-400', body: 'text-[#92400E] dark:text-amber-400', icon: 'text-[#D97706]' }
        : { border: 'border-[#FCA5A5] dark:border-red-900/50', bg: 'bg-[#FEF2F2] dark:bg-red-900/10', head: 'text-[#B91C1C] dark:text-red-400', body: 'text-[#991B1B] dark:text-red-400', icon: 'text-[#DC2626]' };
    return (
        <div className={`rounded-xl border-2 ${t.border} ${t.bg} p-4 flex flex-col gap-1.5`}>
            <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${t.icon}`} />
                <h3 className={`text-[11px] font-extrabold tracking-wide ${t.head} uppercase`}>{title}</h3>
            </div>
            <p className={`text-[15px] font-bold ${t.body} leading-snug`}>{value}</p>
        </div>
    );
};

/**
 * Full-screen "landscape" emergency panel for the dashboard. Zooms out from the
 * Emergency button over a blurred backdrop and shows the EXACT same fields as the
 * mobile app's Emergency Info sheet — fetched from the same endpoint
 * (GET /residences/:id/emergency-info). Fast, low-decoration: built for real use.
 */
const EmergencyInfoModal = ({ residenceId, residentName, origin, onClose }) => {
    const [show, setShow] = useState(false);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Animate in
    useEffect(() => {
        const r = requestAnimationFrame(() => setShow(true));
        return () => cancelAnimationFrame(r);
    }, []);

    // Fetch the same emergency payload the mobile app uses
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await residencesService.getEmergencyInfo(residenceId);
                if (!alive) return;
                setInfo(res?.data || {});
            } catch (e) {
                if (!alive) return;
                setError('Could not load emergency info');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [residenceId]);

    const handleClose = useCallback(() => {
        setShow(false);
        const t = setTimeout(onClose, 190);
        return () => clearTimeout(t);
    }, [onClose]);

    // ESC + lock background scroll
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [handleClose]);

    const i = info || {};
    const nok = i.nextOfKin || {};
    const gp = i.gp || {};
    const provider = i.careProvider || {};

    // Compose the same combined lines the mobile sheet builds.
    const nokLine = [
        has(nok.name) ? nok.name : null,
        has(nok.relationship) ? `(${nok.relationship})` : null,
    ].filter(Boolean).join(' ');
    const nokFull = [has(nokLine) ? nokLine : null, has(nok.phone) ? nok.phone : null]
        .filter(Boolean).join(' — ');
    const gpFull = [gp.name, gp.address].filter(has).join(' — ');
    const providerFull = [provider.name, provider.phone].filter(has).join(' — ');
    const keyRisks = Array.isArray(i.keyRisks) ? i.keyRisks : [];

    const animStyle = {
        transformOrigin: origin ? `${origin.x}px ${origin.y}px` : 'center',
        transform: show ? 'scale(1)' : 'scale(0.12)',
        opacity: show ? 1 : 0,
        transition: 'transform 190ms ease-out, opacity 190ms ease-out',
    };

    return (
        <div className="fixed inset-0 z-[100]">
            {/* Blurred backdrop */}
            <div
                onClick={handleClose}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-200"
                style={{ opacity: show ? 1 : 0 }}
            />

            {/* Scaling layer — full viewport so transform-origin maps to button coords */}
            <div
                className="absolute inset-0 flex items-center justify-center p-3 sm:p-5"
                style={animStyle}
                onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
            >
                <div className="relative w-full max-w-[960px] max-h-[88vh] bg-[#F8FAFC] dark:bg-[#262b33] rounded-2xl shadow-2xl overflow-hidden flex flex-col">

                    {/* ── Red emergency header ── */}
                    <div className="flex items-center justify-between gap-4 px-5 sm:px-7 py-4 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10.5px] font-bold tracking-[0.12em] text-white/80 uppercase">For ambulance &amp; responders</p>
                                <h2 className="text-[20px] sm:text-[22px] font-extrabold leading-tight truncate">
                                    {val(i.name || residentName)}
                                </h2>
                            </div>
                        </div>
                        <div className="flex items-center gap-2.5 shrink-0">
                            <a
                                href="tel:999"
                                className="hidden sm:flex items-center gap-2 bg-white text-[#DC2626] px-4 py-2.5 rounded-xl text-[14px] font-extrabold hover:bg-red-50 transition shadow-sm"
                            >
                                <Phone className="w-4 h-4" /> Call 999
                            </a>
                            <button
                                onClick={handleClose}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* ── Body ── */}
                    <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
                        {loading ? (
                            <div className="h-full flex items-center justify-center">
                                <Loader2 className="w-8 h-8 animate-spin text-[#DC2626]" />
                            </div>
                        ) : error ? (
                            <div className="h-full flex items-center justify-center">
                                <p className="text-[14px] font-medium text-[#DC2626]">{error}</p>
                            </div>
                        ) : (
                            <>
                                {/* Critical clinical alerts — top priority */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <AlertCard icon={ShieldAlert} title="Allergies" value={val(i.allergies)} />
                                    <AlertCard icon={HeartPulse} title="DNACPR status" value={val(i.dnacprStatus)} />
                                    <AlertCard
                                        icon={AlertTriangle}
                                        title="Key risks"
                                        tone="amber"
                                        value={keyRisks.length ? keyRisks.join('   •   ') : '—'}
                                    />
                                </div>

                                {/* Detail columns — same fields as the mobile sheet */}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    <InfoCard icon={User} title="Patient">
                                        <Field label="Name" value={val(i.name || residentName)} strong />
                                        <Field label="Date of birth" value={fmtDob(i.dob)} trailing={i.age != null ? `(${i.age} years)` : null} />
                                        <Field label="Address / room number" value={val(i.addressRoom)} />
                                        <Field label="NHS number" value={val(i.nhsNumber)} />
                                    </InfoCard>

                                    <InfoCard icon={Users} title="Contacts">
                                        <Field label="Next of kin" value={has(nokFull) ? nokFull : '—'} strong />
                                        <Field label="GP details" value={has(gpFull) ? gpFull : '—'} />
                                        <Field label="Care provider contact" value={has(providerFull) ? providerFull : '—'} />
                                    </InfoCard>

                                    <InfoCard icon={Stethoscope} title="Clinical">
                                        <Field label="Medication summary" value={val(i.medicationSummary)} />
                                        <Field label="What to tell ambulance crew" value={val(i.whatToTellAmbulanceCrew)} />
                                    </InfoCard>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Footer: full-width Call 999 ── */}
                    <a
                        href="tel:999"
                        className="shrink-0 flex items-center justify-center gap-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white py-4 transition"
                    >
                        <Phone className="w-5 h-5" />
                        <span className="text-[16px] font-extrabold">Call 999 immediately</span>
                        <span className="text-[12px] font-medium text-white/80">· in a life-threatening emergency</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default EmergencyInfoModal;
